import { useEffect, useMemo, useState } from "react";
import { diffWordsWithSpace } from "diff";
import {
    GitDiff, X, ArrowLeft, ArrowRight, CheckCircle, WarningCircle, MinusCircle, PlusCircle,
} from "@phosphor-icons/react";
import { api, formatApiError } from "./api";
import { Button } from "../components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

/** Renders an inline word-level diff: green for additions, rose for removals. */
function InlineWordDiff({ from, to, side }) {
    const parts = useMemo(() => diffWordsWithSpace(from || "", to || ""), [from, to]);
    return (
        <span className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed">
            {parts.map((p, i) => {
                if (p.added && side === "right") return <ins key={i} className="bg-emerald-100 text-emerald-900 no-underline rounded px-0.5">{p.value}</ins>;
                if (p.removed && side === "left") return <del key={i} className="bg-rose-100 text-rose-900 no-underline rounded px-0.5">{p.value}</del>;
                if (!p.added && !p.removed) return <span key={i}>{p.value}</span>;
                return null;  // hide opposite-side ops
            })}
        </span>
    );
}

function DiffStat({ label, count, tone }) {
    if (count === 0) return null;
    const toneMap = {
        emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
        rose: "bg-rose-50 text-rose-800 border-rose-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
        slate: "bg-slate-50 text-slate-700 border-slate-200",
    };
    return (
        <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${toneMap[tone]}`}>
            {count} {label}
        </span>
    );
}

/**
 * Side-by-side diff dialog comparing the current draft (in-editor state)
 * against the canonical Default T&C set.
 *
 * Props:
 *  - open, onOpenChange : controlled visibility
 *  - currentName        : name of the set being edited
 *  - currentPreamble
 *  - currentClauses    : [{title, body}, ...]
 *  - currentPaymentTerms : [string, ...]
 */
export default function CompareWithDefaultDialog({
    open, onOpenChange,
    currentName, currentPreamble, currentClauses, currentPaymentTerms,
}) {
    const [defaults, setDefaults] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get("/billing/proposal-tnc/master");
                setDefaults(data);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load Default set"));
                onOpenChange(false);
            } finally {
                setLoading(false);
            }
        })();
    }, [open, onOpenChange]);

    const summary = useMemo(() => {
        if (!defaults) return null;
        const dClauses = defaults.clauses || [];
        const dTerms = defaults.payment_terms || [];

        // Match clauses by title (case-insensitive)
        const dByTitle = new Map(dClauses.map((c) => [c.title.toLowerCase().trim(), c]));
        const cByTitle = new Map(currentClauses.map((c) => [c.title.toLowerCase().trim(), c]));

        const added = currentClauses.filter((c) => !dByTitle.has(c.title.toLowerCase().trim()));
        const removed = dClauses.filter((c) => !cByTitle.has(c.title.toLowerCase().trim()));
        const matched = currentClauses
            .map((c) => {
                const def = dByTitle.get(c.title.toLowerCase().trim());
                if (!def) return null;
                const changed = (def.body || "").trim() !== (c.body || "").trim();
                return { current: c, default: def, changed };
            })
            .filter(Boolean);

        const changed = matched.filter((m) => m.changed);
        const unchanged = matched.filter((m) => !m.changed);

        const preambleChanged = (defaults.preamble || "").trim() !== (currentPreamble || "").trim();

        // Payment terms are positional — diff as ordered lists
        const termAddedOrChanged = currentPaymentTerms
            .map((t, i) => ({ idx: i, current: t, default: dTerms[i], differs: (dTerms[i] || "").trim() !== (t || "").trim() }));
        const termsChanged = termAddedOrChanged.filter((r) => r.differs).length;
        const termsCountDelta = currentPaymentTerms.length - dTerms.length;

        return { added, removed, changed, unchanged, preambleChanged, termsChanged, termsCountDelta, dTerms };
    }, [defaults, currentClauses, currentPreamble, currentPaymentTerms]);

    if (!open) return null;
    const isComparingDefault = currentName?.toLowerCase().trim() === "default";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-6xl max-h-[92vh] flex flex-col p-0">
                <DialogHeader className="px-6 py-4 border-b border-slate-200 flex-row items-center gap-3">
                    <GitDiff size={20} weight="fill" className="text-slate-700 shrink-0" />
                    <div className="flex-1">
                        <DialogTitle className="font-display flex items-center gap-2">
                            Compare with Default
                            <span className="font-mono text-xs text-slate-500">{currentName}</span>
                            <ArrowRight size={12} className="text-slate-400" />
                            <span className="font-mono text-xs text-slate-500">Default</span>
                        </DialogTitle>
                        <DialogDescription>
                            Word-level diff against the canonical "Default" T&C set. Use this to spot accidental drift or review legal deviations before save.
                        </DialogDescription>
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5" data-testid="tnc-compare-content">
                    {loading ? (
                        <div className="text-sm text-slate-500">Loading Default set…</div>
                    ) : !defaults ? null : isComparingDefault ? (
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 inline-flex items-start gap-2">
                            <WarningCircle size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                            You're editing the Default set itself — there's nothing to compare against. Make your edits, then if needed compare other sets against this set.
                        </div>
                    ) : (
                        <>
                            {/* Stats strip */}
                            <div className="flex items-center gap-2 flex-wrap" data-testid="tnc-compare-stats">
                                <DiffStat label="changed" count={summary.changed.length} tone="amber" />
                                <DiffStat label="added" count={summary.added.length} tone="emerald" />
                                <DiffStat label="removed" count={summary.removed.length} tone="rose" />
                                <DiffStat label="unchanged" count={summary.unchanged.length} tone="slate" />
                                <span className="ml-auto text-[11px] text-slate-500 inline-flex items-center gap-1.5">
                                    Terms changed: {summary.termsChanged}
                                    {summary.termsCountDelta !== 0 && <span className={summary.termsCountDelta > 0 ? "text-emerald-700" : "text-rose-700"}>({summary.termsCountDelta > 0 ? "+" : ""}{summary.termsCountDelta} count)</span>}
                                </span>
                            </div>

                            {/* Preamble */}
                            {summary.preambleChanged && (
                                <DiffSection title="Preamble" tone="amber">
                                    <SideBySide
                                        leftLabel="Default"
                                        rightLabel={currentName}
                                        leftBody={defaults.preamble}
                                        rightBody={currentPreamble}
                                    />
                                </DiffSection>
                            )}

                            {/* Payment terms */}
                            {(summary.termsChanged > 0 || summary.termsCountDelta !== 0) && (
                                <DiffSection title="Payment Terms & Other Conditions" tone="amber">
                                    <PaymentTermsDiff
                                        leftItems={summary.dTerms}
                                        rightItems={currentPaymentTerms}
                                    />
                                </DiffSection>
                            )}

                            {/* Changed clauses */}
                            {summary.changed.map(({ current, default: def }) => (
                                <DiffSection key={`chg-${def.order}`} title={`Clause: ${def.title}`} tone="amber" testid={`tnc-compare-clause-changed-${def.order}`}>
                                    <SideBySide
                                        leftLabel="Default"
                                        rightLabel={currentName}
                                        leftBody={def.body}
                                        rightBody={current.body}
                                    />
                                </DiffSection>
                            ))}

                            {/* Added clauses (only in current) */}
                            {summary.added.map((c, i) => (
                                <DiffSection key={`add-${i}`} title={`Added: ${c.title}`} tone="emerald" icon={PlusCircle} testid={`tnc-compare-clause-added-${i}`}>
                                    <div className="text-[11px] font-mono whitespace-pre-wrap leading-relaxed text-slate-800 bg-emerald-50/60 border border-emerald-200 rounded px-3 py-2">
                                        {c.body}
                                    </div>
                                </DiffSection>
                            ))}

                            {/* Removed clauses (only in default) */}
                            {summary.removed.map((c) => (
                                <DiffSection key={`rem-${c.order}`} title={`Removed from Default: ${c.title}`} tone="rose" icon={MinusCircle} testid={`tnc-compare-clause-removed-${c.order}`}>
                                    <div className="text-[11px] font-mono whitespace-pre-wrap leading-relaxed text-slate-800 bg-rose-50/60 border border-rose-200 rounded px-3 py-2">
                                        {c.body}
                                    </div>
                                </DiffSection>
                            ))}

                            {/* Empty state */}
                            {summary.changed.length === 0
                                && summary.added.length === 0
                                && summary.removed.length === 0
                                && !summary.preambleChanged
                                && summary.termsChanged === 0
                                && summary.termsCountDelta === 0 && (
                                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 inline-flex items-start gap-2" data-testid="tnc-compare-no-changes">
                                    <CheckCircle size={16} weight="fill" className="text-emerald-700 shrink-0 mt-0.5" />
                                    No deviations detected — this set is identical to the Default.
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
                    <Button onClick={() => onOpenChange(false)} variant="outline" className="gap-1.5" data-testid="tnc-compare-close-btn">
                        <X size={14} /> Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function DiffSection({ title, tone = "slate", icon, testid, children }) {
    const Icon = icon;
    const toneMap = {
        amber: { dot: "bg-amber-400", chip: "bg-amber-50 text-amber-800 border-amber-200" },
        emerald: { dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
        rose: { dot: "bg-rose-500", chip: "bg-rose-50 text-rose-800 border-rose-200" },
        slate: { dot: "bg-slate-400", chip: "bg-slate-50 text-slate-700 border-slate-200" },
    };
    const T = toneMap[tone];
    return (
        <section className="space-y-2" data-testid={testid}>
            <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${T.dot}`} />
                <h4 className="font-display font-bold text-sm text-slate-900">{title}</h4>
                {Icon && <Icon size={14} weight="fill" className={tone === "emerald" ? "text-emerald-600" : "text-rose-600"} />}
            </div>
            <div>{children}</div>
        </section>
    );
}

function SideBySide({ leftLabel, rightLabel, leftBody, rightBody }) {
    return (
        <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 inline-flex items-center gap-1.5">
                    <ArrowLeft size={10} /> {leftLabel}
                </div>
                <div className="px-3 py-2 max-h-72 overflow-y-auto">
                    <InlineWordDiff from={leftBody} to={rightBody} side="left" />
                </div>
            </div>
            <div className="rounded-md border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 inline-flex items-center gap-1.5">
                    <ArrowRight size={10} /> {rightLabel} (current)
                </div>
                <div className="px-3 py-2 max-h-72 overflow-y-auto">
                    <InlineWordDiff from={leftBody} to={rightBody} side="right" />
                </div>
            </div>
        </div>
    );
}

function PaymentTermsDiff({ leftItems, rightItems }) {
    const max = Math.max(leftItems.length, rightItems.length);
    const rows = [];
    for (let i = 0; i < max; i++) {
        const l = leftItems[i];
        const r = rightItems[i];
        if (l == null && r != null) {
            rows.push({ kind: "added", idx: i, right: r });
        } else if (r == null && l != null) {
            rows.push({ kind: "removed", idx: i, left: l });
        } else if ((l || "").trim() !== (r || "").trim()) {
            rows.push({ kind: "changed", idx: i, left: l, right: r });
        }
    }
    if (rows.length === 0) return null;
    return (
        <div className="rounded-md border border-slate-200 overflow-hidden">
            <table className="w-full text-[11px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                        <th className="px-3 py-1.5 text-left font-bold uppercase tracking-wider text-slate-600 text-[10px] w-12">#</th>
                        <th className="px-3 py-1.5 text-left font-bold uppercase tracking-wider text-slate-600 text-[10px]">Default</th>
                        <th className="px-3 py-1.5 text-left font-bold uppercase tracking-wider text-slate-600 text-[10px]">Current</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.idx} className="border-t border-slate-100 align-top">
                            <td className="px-3 py-2 font-mono text-slate-500">{r.idx + 1}</td>
                            <td className="px-3 py-2 w-1/2">
                                {r.left
                                    ? <InlineWordDiff from={r.left} to={r.right || ""} side="left" />
                                    : <span className="text-slate-400 italic">— not in Default —</span>}
                            </td>
                            <td className="px-3 py-2 w-1/2">
                                {r.right
                                    ? <InlineWordDiff from={r.left || ""} to={r.right} side="right" />
                                    : <span className="text-slate-400 italic">— removed —</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
